import React, { useState, useEffect } from 'react';
import axios from 'axios';

const RevistaPagamentos = ({
  API,
  getAuthHeaders,
  setCurrentView
}) => {
  const [relatorio, setRelatorio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTurma, setFiltroTurma] = useState('');
  const [filtroAluno, setFiltroAluno] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos'); // todos, pagos, pendentes

  useEffect(() => {
    loadRelatorio();
  }, []);

  const loadRelatorio = async () => {
    // Só carregar se estiver autenticado
    const token = localStorage.getItem('ebd_token');
    if (!token) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API}/revistas/relatorio-pagamentos`, { headers: getAuthHeaders() });
      setRelatorio(response.data);
    } catch (error) {
      alert('Erro ao carregar relatório de pagamentos');
    } finally {
      setLoading(false);
    }
  };

  const togglePagamento = async (revistaId, alunoId, turmaId, valorRevista) => {
    try {
      const pagamento = {
        revista_id: revistaId,
        aluno_id: alunoId,
        turma_id: turmaId,
        valor_pago: valorRevista,
        data_pagamento: new Date().toISOString().split('T')[0],
        status: 'pago'
      };

      await axios.post(`${API}/revistas/pagamento`, pagamento, { headers: getAuthHeaders() });
      await loadRelatorio(); // Recarregar dados
    } catch (error) {
      alert('Erro ao registrar pagamento');
    }
  };

  const removerPagamento = async (revistaId, alunoId) => {
    if (!window.confirm('Deseja realmente desmarcar este pagamento?')) {
      return;
    }

    try {
      await axios.delete(`${API}/revistas/pagamento/${revistaId}/${alunoId}`, { headers: getAuthHeaders() });
      await loadRelatorio(); // Recarregar dados
      alert('Pagamento desmarcado com sucesso!');
    } catch (error) {
      alert('Erro ao desmarcar pagamento');
    }
  };

  // Função para filtrar dados
  const filtrarDados = (relatorio) => {
    return relatorio.map(revista => {
      const turmasFiltradas = revista.turmas
        .filter(turma => {
          // Filtro por turma
          if (filtroTurma && !turma.nome.toLowerCase().includes(filtroTurma.toLowerCase())) {
            return false;
          }
          return true;
        })
        .map(turma => {
          const alunosFiltrados = turma.alunos.filter(aluno => {
            // Filtro por nome do aluno
            if (filtroAluno && !aluno.nome.toLowerCase().includes(filtroAluno.toLowerCase())) {
              return false;
            }

            // Filtro por status
            if (filtroStatus === 'pagos' && aluno.status_pagamento !== 'pago') {
              return false;
            }
            if (filtroStatus === 'pendentes' && aluno.status_pagamento === 'pago') {
              return false;
            }

            return true;
          });

          return { ...turma, alunos: alunosFiltrados };
        })
        .filter(turma => turma.alunos.length > 0); // Remove turmas sem alunos após filtro

      return { ...revista, turmas: turmasFiltradas };
    }).filter(revista => revista.turmas.length > 0); // Remove revistas sem turmas após filtro
  };

  const dadosFiltrados = filtrarDados(relatorio);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⟳</div>
          <p>Carregando relatório...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <button
              onClick={() => setCurrentView('historico')}
              className="self-start px-3 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-200 text-sm font-medium"
            >
              ← Voltar ao Faturamento
            </button>

            <div className="text-center sm:flex-1 sm:mx-6">
              <h1 className="text-xl sm:text-2xl font-bold">💳 Controle de Pagamento - Revistas</h1>
              <p className="text-blue-200 text-sm mt-1">Gerencie pagamentos das revistas por aluno</p>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filtros */}
        {relatorio.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">🔍 Filtros</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Filtro por Turma */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por Turma:</label>
                <input
                  type="text"
                  value={filtroTurma}
                  onChange={(e) => setFiltroTurma(e.target.value)}
                  placeholder="Digite o nome da turma..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Filtro por Aluno */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por Aluno:</label>
                <input
                  type="text"
                  value={filtroAluno}
                  onChange={(e) => setFiltroAluno(e.target.value)}
                  placeholder="Digite o nome do aluno..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Filtro por Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status de Pagamento:</label>
                <select
                  value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="todos">Todos</option>
                  <option value="pagos">Somente Pagos</option>
                  <option value="pendentes">Somente Pendentes</option>
                </select>
              </div>
            </div>

            {/* Botão Limpar Filtros */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setFiltroTurma('');
                  setFiltroAluno('');
                  setFiltroStatus('todos');
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {relatorio.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Nenhuma revista encontrada</h3>
            <p className="text-gray-500">Não há revistas com valor configurado no sistema.</p>
          </div>
        ) : dadosFiltrados.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Nenhum resultado encontrado</h3>
            <p className="text-gray-500">Ajuste os filtros para encontrar os dados desejados.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {dadosFiltrados.map(revista => (
              <div key={revista.revista_id} className="bg-white rounded-lg shadow-lg p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-gray-800">{revista.tema}</h3>
                  {revista.valor > 0 && (
                    <p className="text-green-600 font-semibold">
                      Valor: R$ {revista.valor.toFixed(2).replace('.', ',')}
                    </p>
                  )}
                </div>

                {revista.turmas.map(turma => (
                  <div key={turma.id} className="mb-6">
                    <h4 className="text-md font-semibold text-blue-600 mb-3">
                      📖 {turma.nome} ({turma.alunos.length} alunos)
                    </h4>

                    {turma.alunos.length === 0 ? (
                      <p className="text-gray-500 text-sm">Nenhum aluno cadastrado nesta turma</p>
                    ) : (
                      <div className="grid gap-2">
                        {turma.alunos.map(aluno => (
                          <div key={aluno.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <span className="font-medium">{aluno.nome}</span>
                              {aluno.status_pagamento === 'pago' && (
                                <span className="ml-2 text-xs text-green-600">
                                  (Pago em {aluno.data_pagamento})
                                </span>
                              )}
                            </div>

                            <div className="flex gap-2">
                              {aluno.status_pagamento === 'pago' ? (
                                <>
                                  <span className="px-4 py-2 bg-green-100 text-green-700 border border-green-300 rounded-lg text-sm font-medium">
                                    ✓ Pago
                                  </span>
                                  <button
                                    onClick={() => removerPagamento(revista.revista_id, aluno.id)}
                                    className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                                  >
                                    Desmarcar
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => togglePagamento(revista.revista_id, aluno.id, turma.id, revista.valor)}
                                  className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                                >
                                  Registrar Pagamento
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {revista.valor === 0 && (
                  <div className="text-center py-4 bg-yellow-50 rounded-lg">
                    <p className="text-yellow-700">Esta revista é gratuita (valor R$ 0,00)</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RevistaPagamentos;
